/**
 * Lightweight API for fetching workflow node dropdown labels.
 *
 * This is intentionally kept separate from logiblocksAPI.ts because that file
 * imports from the Logiblocks serialization layer, which in turn imports the
 * node registry, which imports node customisation components — creating a
 * circular dependency if those components also imported from logiblocksAPI.ts.
 */
import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse } from '@/apis/types';

/** A single option item returned by the dropdown-labels endpoint. */
export interface DropdownLabelItem {
  id: string;
  label: string;
}

/**
 * Per-node dropdown label data as returned by the backend.
 * Fields match the actual API response keys exactly.
 */
export interface NodeDropdownLabelMap {
  couriers?: DropdownLabelItem[];
  teams?: DropdownLabelItem[];
  origins?: DropdownLabelItem[];
  destinations?: DropdownLabelItem[];
  alert_types?: DropdownLabelItem[];
  products?: DropdownLabelItem[];
  location?: DropdownLabelItem[];
}

/** Response wrapper — `nodes` keys are ReactFlow node IDs e.g. "alertTrigger:<uuid>". */
interface WorkflowNodeDropdownLabelsData {
  nodes: Record<string, NodeDropdownLabelMap>;
}

/** Convenience alias — the unwrapped nodes map. */
export type WorkflowNodeDropdownLabels = Record<string, NodeDropdownLabelMap>;

/**
 * Fetch the saved dropdown option labels for every node in a workflow.
 * Call this when opening the customisation side panel so that paginated
 * dropdowns can show labels for values that live beyond page 1.
 *
 * @param token - Authentication token
 * @param workflowId - UUID of the workflow
 */
export const getWorkflowNodeDropdownLabels = async (
  token: string,
  workflowId: string
): Promise<WorkflowNodeDropdownLabels> => {
  const url = `${APIPaths.getWorkflowNodeDropdownLabels}${workflowId}/`;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<WorkflowNodeDropdownLabelsData>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to get workflow node dropdown labels: ${response.data.data.message || 'Unknown error'}`
    );
  }

  return response.data.data.data?.nodes ?? {};
};
